import { findMatches } from '@/ds';
import { fileName, toCsv } from '@/lib/download/csv';
import { ageOn } from '@/lib/format/format';
import { compareSerbian } from '@/lib/search/collate';
import type { InterestedRunner, OrganizationEvent } from './api';

export type RunnerSort = 'name' | 'email' | 'age';
export const RUNNER_SORTS = ['name', 'email', 'age'] as const satisfies readonly RunnerSort[];
export type Direction = 'asc' | 'desc';

/** A runner as the list shows them: the profile's name and the age today, never the birth date. */
export interface RunnerRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  age: number | null;
}

const blank = (value: string | null) => (value?.trim() ? value.trim() : null);

export const toRunnerRows = (runners: readonly InterestedRunner[], today: string): RunnerRow[] =>
  runners.map((r) => ({
    id: r.interestId,
    firstName: blank(r.firstName),
    lastName: blank(r.lastName),
    email: r.email,
    age: ageOn(r.dateOfBirth, today),
  }));

/** "Jovana Đurić", or null when the profile has no name. */
export const runnerName = (row: RunnerRow): string | null =>
  [row.firstName, row.lastName].filter(Boolean).join(' ') || null;

/** A missing value sorts last whichever way the column runs. */
const nullsLast = <T>(x: T | null, y: T | null, by: (x: T, y: T) => number, turn: number) =>
  x === y ? 0 : x === null ? 1 : y === null ? -1 : turn * by(x, y);

const byEmail = (x: string, y: string) => x.localeCompare(y, 'en', { sensitivity: 'base' });

/** The chosen column in its direction, then ties by last and first name, then email, A–Z. */
function compare(a: RunnerRow, b: RunnerRow, sort: RunnerSort, dir: Direction): number {
  const turn = dir === 'desc' ? -1 : 1;
  const name = (t: number) =>
    nullsLast(a.lastName, b.lastName, compareSerbian, t) ||
    nullsLast(a.firstName, b.firstName, compareSerbian, t);
  const email = (t: number) => t * byEmail(a.email, b.email);
  if (sort === 'email') return email(turn) || name(1);
  if (sort === 'age') return nullsLast(a.age, b.age, (x, y) => x - y, turn) || name(1) || email(1);
  return name(turn) || email(1);
}

export const sortRunners = (rows: readonly RunnerRow[], sort: RunnerSort, dir: Direction) =>
  [...rows].sort((a, b) => compare(a, b, sort, dir));

/** By name (either order) or email, across script, case and diacritics: "djuric" finds Ђурић. */
export function findRunners(rows: readonly RunnerRow[], query: string | undefined): RunnerRow[] {
  if (!query) return [...rows];
  // "dj" is how đ is typed without it.
  const queries = /dj/i.test(query) ? [query, query.replace(/dj/gi, 'đ')] : [query];
  return rows.filter((row) =>
    [
      [row.firstName, row.lastName].filter(Boolean).join(' '),
      [row.lastName, row.firstName].filter(Boolean).join(' '),
      row.email,
    ].some((text) => queries.some((q) => findMatches(text, q).length > 0)),
  );
}

/** "by last name, A–Z", "oldest first". */
export function orderWords(sort: RunnerSort, dir: Direction): string {
  if (sort === 'age') return dir === 'asc' ? 'youngest first' : 'oldest first';
  const way = dir === 'asc' ? 'A–Z' : 'Z–A';
  return sort === 'email' ? `by email, ${way}` : `by last name, ${way}`;
}

export const CSV_HEADER = ['First name', 'Last name', 'Email', 'Age'] as const;

/** The file's text: the rows in the order given, the age as of the day of the export. */
export const runnersCsv = (rows: readonly RunnerRow[]): string =>
  toCsv([CSV_HEADER, ...rows.map((r) => [r.firstName, r.lastName, r.email, r.age])]);

/** "32. Beogradski maraton 2026-04-19 interested runners.csv", with a search in brackets. */
export const runnersFileName = (
  event: Pick<OrganizationEvent, 'name' | 'date'>,
  query?: string,
): string =>
  `${fileName(`${event.name} ${event.date.slice(0, 10)} interested runners${query ? ` (${query})` : ''}`)}.csv`;
